import { useState, useEffect, ReactNode } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { IconButton } from '@material-ui/core'

import pdfIcon from '../../assets/images/pdf.png'
import sheetsIcon from '../../assets/images/sheets.png'
import defaultImage from '../../assets/images/default-image.png'

import { Icon } from 'components'
import * as S from './styled'

export type FileProps = {
  src: string
  filename: string
}

type FileUploadProps = {
  file: FileProps
  name: string
  variant?: 'circle' | 'square'
  onLoadImage?: (_files: File[], _field: string) => void
  onRemoveImage?: (_name: string) => void
  acceptTypes?: 'images' | 'pdf' | 'sheets'
  error?: boolean
  helperText?: ReactNode
  maxSize?: number
}

const FILE_TYPES_ACCEPT = {
  images: ['image/jpeg', 'image/png'],
  pdf: ['application/pdf'],
  sheets: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv'
  ]
}

export function FileUpload({
  file = { src: '', filename: '' },
  name,
  variant = 'circle',
  onLoadImage,
  onRemoveImage,
  acceptTypes = 'images',
  error,
  helperText,
  maxSize = 1
}: FileUploadProps) {
  const { t } = useTranslation()
  const [previewImage, setPreviewImage] = useState<string | null>(file.src)
  const [fileType, setFileType] = useState<'image' | 'pdf' | 'sheets' | null>(
    null
  )

  useEffect(() => {
    if (file && file.src && file.src !== '') {
      const fileExtension = file.filename.split('.').pop().toLowerCase()

      if (
        acceptTypes === 'images' &&
        ['jpg', 'jpeg', 'png'].includes(fileExtension)
      ) {
        setPreviewImage(file.src)
        setFileType('image')
      } else if (acceptTypes === 'pdf' && fileExtension === 'pdf') {
        setPreviewImage(file.src)
        setFileType('pdf')
      } else if (
        acceptTypes === 'sheets' &&
        ['xls', 'xlsx', 'csv'].includes(fileExtension)
      ) {
        setPreviewImage(sheetsIcon)
        setFileType('sheets')
      }
      return
    }
    setPreviewImage('')
    setFileType(null)
  }, [file, acceptTypes])

  const onDrop = (acceptedFiles: File[]) => {
    const fileToLoad = acceptedFiles[0]

    if (!fileToLoad) {
      alert(t('fileUpload:validFile'))
      return
    }

    if (
      acceptTypes === 'images' &&
      FILE_TYPES_ACCEPT.images.includes(fileToLoad.type)
    ) {
      const fileURL = URL.createObjectURL(fileToLoad)
      setPreviewImage(fileURL)
      setFileType('image')
    } else if (
      acceptTypes === 'pdf' &&
      FILE_TYPES_ACCEPT.pdf.includes(fileToLoad.type)
    ) {
      setPreviewImage(pdfIcon)
      setFileType('pdf')
    } else if (
      acceptTypes === 'sheets' &&
      FILE_TYPES_ACCEPT.sheets.includes(fileToLoad.type)
    ) {
      setPreviewImage(sheetsIcon)
      setFileType('sheets')
    } else {
      alert(t('fileUpload:validFile'))
      return
    }

    if (onLoadImage) {
      onLoadImage(acceptedFiles, name)
    }
  }

  const accept = FILE_TYPES_ACCEPT[acceptTypes].join(', ')

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop,
    accept,
    maxFiles: 1,
    maxSize: maxSize * 1024 * 1024
  })

  const handleRemoveFile = () => {
    setPreviewImage(null)
    setFileType(null)
    if (onRemoveImage) {
      onRemoveImage(name)
    }
  }

  return (
    <>
      {!previewImage ? (
        <>
          <S.Wrapper
            style={
              isDragActive
                ? { borderColor: '#315CA7' }
                : { borderColor: '#d9d9d9' }
            }
          >
            <S.Title>{t(`fileUpload:${name}`)}</S.Title>
            <S.DropContent {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <S.GridMain onClick={open}>
                <S.GridIcon>
                  <Icon icon="mini-upload" />{' '}
                </S.GridIcon>
                <S.GridAdditional>
                  <S.Description>
                    {t('fileUpload:instructionText')}
                  </S.Description>
                  {acceptTypes === 'images' && (
                    <>
                      <S.Additional>
                        {t('fileUpload:maxImageDimensions')}
                      </S.Additional>{' '}
                    </>
                  )}
                  <S.Additional>
                    {t('fileUpload:maxSizeText') + maxSize + 'MB'}
                  </S.Additional>
                </S.GridAdditional>
              </S.GridMain>
            </S.DropContent>
          </S.Wrapper>

          {error && helperText && (
            <S.AlertContainer>
              <S.AlertIcon>
                <Icon icon="itens-interface-alert" height={10} width={10} />
              </S.AlertIcon>
              <S.AlertText>{helperText}</S.AlertText>
            </S.AlertContainer>
          )}
        </>
      ) : (
        <S.Wrapper>
          <S.Remove>
            <S.RemoveFile>
              <IconButton
                style={{ backgroundColor: '#ffffff' }}
                aria-label="Delete file"
                component="span"
                onClick={handleRemoveFile}
              >
                <Icon
                  icon="dropdown-delete"
                  height={16}
                  width={16}
                  fill="#de4f4f"
                />
              </IconButton>
            </S.RemoveFile>
          </S.Remove>
          <S.GridMain>
            {fileType === 'image' && (
              <>
                {variant === 'circle' && (
                  <img
                    src={previewImage || defaultImage}
                    alt="Preview"
                    style={{
                      borderRadius: '50%',
                      width: '100px',
                      height: '100px',
                      objectFit: 'contain'
                    }}
                  />
                )}

                {variant === 'square' && (
                  <img
                    src={previewImage || defaultImage}
                    alt="Preview"
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </>
            )}

            {fileType === 'pdf' && (
              <S.PDFContainer>
                <img
                  src={pdfIcon}
                  alt="Preview"
                  style={{ width: '100px', height: '100px' }}
                />
                <S.PDFName>{file.filename}</S.PDFName>
              </S.PDFContainer>
            )}

            {fileType === 'sheets' && (
              <S.PDFContainer>
                <img
                  src={sheetsIcon}
                  alt="Preview"
                  style={{ width: '100px', height: '100px' }}
                />
                <S.PDFName>{file.filename}</S.PDFName>
              </S.PDFContainer>
            )}
          </S.GridMain>
        </S.Wrapper>
      )}
    </>
  )
}
